import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ButtonComponent } from '../../../molecules/button/button.component';
import { ButtonType } from '../../../../shared/enums/button-type.enums';
import { IconComponent } from '../../../atoms/icon/icon.component';
import { LinkComponent } from '../../../atoms/link/link.component';
import LinkType from '../../../../shared/enums/link-type.enums';
import { Colors, Sizes } from '../../../../shared/enums/shared.enums';
import {
  EventItem,
  Paragraph,
} from '../../../../shared/interfaces/termine-item.interface';
import { HrefType } from '../../../../shared/enums/href-type.enums';
import {
  CaasComponents,
  CaasLayout,
} from '../../../../shared/interfaces/caas/caas.interface';
import { CaasBodytextService } from '../../../../services/caas-bodytext.service';
import { ShowMoreLessComponent } from '../../../organisms/show-more-less/show-more-less.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CapitalizePipe } from '../../../capitalize.pipe';
import { BaseAccordionComponent } from '../base-accordion.component';
import { ErrorHandlerService } from '../../../../services/error-handler.service';
import { safeAssign } from '../../../../shared/utils/safe-assign.util';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'hdi-termine',
  standalone: true,
  templateUrl: './termine.component.html',
  styleUrls: ['./termine.component.scss'],
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    LinkComponent,
    ShowMoreLessComponent,
    CapitalizePipe,
    NgxSkeletonLoaderModule,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class TermineComponent
  extends BaseAccordionComponent
  implements OnInit, OnChanges
{
  @Input() caasData?: CaasComponents['value'][number];
  @Input() eventsData?: CaasLayout[];
  @Input() eventItems: EventItem[] = [];
  @Input() labelCalendar: string = 'Weiterbildungstermine';
  @Input() bgColor?: Colors;

  @ViewChild('event_content', { static: true }) AccordionToggle: ElementRef =
    {} as ElementRef;

  LinkType = LinkType;
  color = Colors;
  size = Sizes;
  HrefType = HrefType;

  accordion: ButtonType = ButtonType.OPEN_CLOSE_SECONDARY;
  events: CaasLayout[] = [];

  public isLoading = true;

  constructor(
    protected override cdr: ChangeDetectorRef,
    private readonly caasBodytextService: CaasBodytextService,
    protected override route: ActivatedRoute,
    @Inject(DOCUMENT) protected override document: Document,
    protected override router: Router,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    super(cdr, route, document, router);
  }

  protected override getItems() {
    return this.eventItems;
  }

  protected override hasCaasData(): boolean {
    return !!this.caasData;
  }

  protected override getItemId(item: EventItem): string {
    return item.id;
  }

  protected override processCaasData(): void {
    if (Array.isArray(this.caasData) && this.caasData.length) {
      this.setCaasContent(this.caasData[0]);
    }

    if (this.eventsData?.length && this.caasData) {
      this.setCaasEvents(this.caasData);
      this.setCaasContent(this.caasData);
      this.sortEventsByDate();
    }

    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
    }
  }

  setCaasIsOpen(caasData: CaasComponents['value'][number]) {
    if (!this.eventsData || this.eventsData.length === 0) {
      this.isOpen = [];
      this.cdr.detectChanges();
      return;
    }
    this.isOpen = new Array(this.eventsData?.length ?? 0).fill(false);

    if (caasData?.formData?.st_expand?.value?.identifier === 'firstOpen') {
      this.isOpen[0] = true;
    }

    if (caasData?.formData?.st_expand?.value?.identifier === 'allOpen') {
      this.isOpen.fill(true);
    }

    this.cdr.detectChanges();
  }

  extractEventData(data: CaasLayout): EventItem {
    try {
      const formData = data?.['formData'];
      if (!formData) {
        throw new Error(
          'Termine: extractEventData formData is null or undefined'
        );
      }

      let eventDate: Date | null = null;

      const result: Partial<EventItem> = {
        id: '',
        time: '',
        headline: '',
        preheadline: '',
        day: '',
        month: '',
        year: '',
        timestamp: 0,
        content: [],
        speakers: [],
        educationTime: '',
        key: data?.identifier ?? '',
        start_time: '',
        end_time: '',
        link: {
          url: '',
          type: LinkType.INLINE,
          label: '',
          hrefType: HrefType.ANGULAR_ROUTE,
          follow: '',
          target: '',
          title: '',
        },
      };

      const assign = (fn: () => void, label: string) =>
        safeAssign(fn, `Assigning ${label}`, this.errorHandler);

      assign(() => {
        result.id = formData.tt_anchor_name?.value || '';
      }, 'id');

      assign(() => {
        eventDate = formData.tt_date?.value
          ? new Date(formData.tt_date.value as string)
          : null;
        result.time = eventDate ? eventDate.toLocaleTimeString() : '';
      }, 'eventDate');

      assign(() => {
        result.headline = formData.tt_header?.value ?? '';
      }, 'headline');
      assign(() => {
        result.preheadline = formData.tt_preheader?.value ?? '';
      }, 'preheadline');
      assign(() => {
        result.day = eventDate ? String(eventDate.getDate()) : '';
      }, 'day');
      assign(() => {
        result.month = eventDate
          ? eventDate
              .toLocaleString('default', { month: 'short' })
              .toUpperCase()
          : '-';
      }, 'month');
      assign(() => {
        result.year = eventDate ? String(eventDate.getFullYear()) : '-';
      }, 'year');
      assign(() => {
        result.timestamp = eventDate ? eventDate.getTime() : 0;
      }, 'timestamp');
      assign(() => {
        result.content = this.parseContent(formData.tt_contents?.value ?? '');
      }, 'content');
      assign(() => {
        result.speakers = formData.tt_speakers?.value ?? '';
      }, 'speakers');
      assign(() => {
        result.educationTime = formData.tt_eligible_time?.value ?? '';
      }, 'educationTime');
      assign(() => {
        result.start_time = formData.tt_starttime?.value ?? '';
      }, 'start_time');
      assign(() => {
        result.end_time = formData.tt_endtime?.value ?? '';
      }, 'end_time');

      assign(() => {
        const linkForm = formData.tt_application_link?.value?.formData;
        let linkUrl: string = '';
        let linkType: LinkType = LinkType.INLINE;
        let linkLabel: string = '';
        let linkHrefType: HrefType = HrefType.ANGULAR_ROUTE;
        let linkFollow: string = '';
        let linkTarget: string = '';
        let linkTitle: string = '';
        assign(() => {
          linkUrl =
            linkForm?.lt_link_external?.value ||
            linkForm?.lt_link_internal?.value?.uid?.toString() ||
            '';
        }, 'linkUrl');
        assign(() => {
          linkType = linkForm?.lt_link_external?.value
            ? LinkType.EXTERNAL
            : LinkType.INLINE;
        }, 'linkType');
        assign(() => {
          linkLabel = linkForm?.lt_link_label?.value ?? '';
        }, 'linkLabel');
        assign(() => {
          linkHrefType = linkForm?.lt_link_external?.value
            ? HrefType.EXTERNAL
            : HrefType.ANGULAR_ROUTE;
        }, 'linkHrefType');
        assign(() => {
          linkFollow = linkForm?.lt_link_follow?.value ?? '';
        }, 'linkFollow');
        assign(() => {
          linkTarget =
            linkForm?.lt_link_target?.value?.identifier?.toString() === 'new'
              ? '_blank'
              : '_self';
        }, 'linkTarget');
        assign(() => {
          linkTitle = linkForm?.lt_link_title?.value ?? '';
        }, 'linkTitle');
        if (result?.link) {
          result.link.url = linkUrl;
          result.link.type = linkType;
          result.link.label = linkLabel;
          result.link.hrefType = linkHrefType;
          result.link.follow = linkFollow;
          result.link.target = linkTarget;
          result.link.title = linkTitle;
        }
      }, 'link');

      return result as EventItem;
    } catch (error) {
      console.info('Termine: Error extracting event data:', error, data);
      return {} as EventItem;
    }
  }

  parseContent(htmlContent: string): Paragraph[] {
    try {
      const formattedContent =
        this.caasBodytextService.extractTextEditorContent(htmlContent) || '';
      return [{ text: formattedContent }];
    } catch (error) {
      console.info('Termine: Error parsing content:', error);
      return [{ text: '' }];
    }
  }

  setCaasContent(caasData: CaasComponents['value'][number]) {
    safeAssign(
      () => {
        this.title = caasData?.formData?.st_header?.value ?? 'Termine';
      },
      'Assigning title',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.labelCalendar =
          caasData?.formData?.st_subheader?.value ?? 'Weiterbildungstermine';
      },
      'Assigning labelCalendar',
      this.errorHandler
    );
  }

  setCaasEvents(caasData: CaasComponents['value'][number]) {
    if (!Array.isArray(this.eventsData) || this.eventsData.length === 0) {
      console.info('Termine: No events data available to process.');
      return;
    }

    safeAssign(
      () => {
        this.eventItems = (this.eventsData ?? []).map((event) =>
          this.extractEventData(event)
        );
      },
      'Assigning eventItems',
      this.errorHandler
    );
    this.setCaasIsOpen(caasData);
  }

  formatDayTime(event: EventItem): string {
    // Safely retrieve locale or use default
    const locale = this.caasData?.formData?.locale?.identifier || 'de-DE';

    try {
      const formatter = new Intl.DateTimeFormat(locale, { month: 'short' });

      // Generate month mapping dynamically
      const months = Array.from({ length: 12 }, (_, index) =>
        formatter.format(new Date(2024, index, 1)).toUpperCase()
      );

      // Parse month and day
      const monthIndex = months.indexOf(event.month.toUpperCase());
      const day = Number(event.day);

      if (monthIndex === -1 || isNaN(day)) {
        console.info('Invalid date components:', event);
        return '-';
      }

      // Create and validate date object
      const eventDate = new Date(new Date().getFullYear(), monthIndex, day);
      if (isNaN(eventDate.getTime())) {
        console.info('Invalid date:', event);
        return '-';
      }

      // Format time details
      let endTimeFormatted = '';
      if (event.end_time) {
        endTimeFormatted = ` - ${this.formatTime(event.end_time)}`;
      }
      const formattedTime = event.start_time
        ? `${this.formatTime(event.start_time)}${endTimeFormatted} Uhr`
        : 'Uhrzeit folgt noch';

      // Format full output
      const weekday = eventDate.toLocaleDateString(locale, { weekday: 'long' });
      return `${weekday}, ${formattedTime} ${event.preheadline || ''}`.trim();
    } catch (error) {
      console.info('Error formatting date/time:', error, event);
      return '-';
    }
  }

  formatTime(isoDateString: string): string {
    try {
      const date = new Date(isoDateString);
      if (isNaN(date.getTime()))
        throw new Error(`Invalid time format: ${isoDateString}`);
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      return `${hours}.${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.info('Error formatting time:', error);
      return '-';
    }
  }

  sortEventsByDate() {
    this.eventItems.sort((a, b) => {
      return a.timestamp - b.timestamp;
    });
  }
}
